import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InboxIcon from '@mui/icons-material/Inbox';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  linearProgressClasses,
  Popover,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BackgroundTask, useBackgroundTasks } from '../../context/BackgroundTasksContext';

const StyledProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  },
}));

interface BackgroundTasksPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const TaskItem: React.FC<{ task: BackgroundTask; onRemoved?: () => void }> = ({
  task,
  onRemoved,
}) => {
  const theme = useTheme();
  const { removeTask, cancelTask, retryTask } = useBackgroundTasks();
  const navigate = useNavigate();

  const isRunning = task.status === 'running';
  const isCompleted = task.status === 'completed';
  const isError = task.status === 'error';
  const hasErrors = isCompleted && task.result && task.result.errorCount > 0;

  const getStatusIcon = () => {
    if (isRunning) {
      return (
        <SyncIcon
          sx={{
            color: theme.palette.primary.main,
            animation: 'spin 2s linear infinite',
            '@keyframes spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            fontSize: 20,
          }}
        />
      );
    }
    if (hasErrors) {
      return <WarningAmberIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />;
    }
    if (isCompleted) {
      return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
    }
    return <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
  };

  const getStatusColor = () => {
    if (isRunning) return theme.palette.primary.main;
    if (isCompleted && !hasErrors) return theme.palette.success.main;
    if (hasErrors) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const formatTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        borderLeft: `3px solid ${getStatusColor()}`,
        mb: 0.5,
        borderRadius: '4px !important',
        background: alpha(getStatusColor(), 0.04),
      }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        sx={{
          minHeight: 40,
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
        }}>
        {getStatusIcon()}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
            {task.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
            {task.fileName}
            {task.createdAt && ` \u00B7 ${formatTime(task.createdAt)}`}
          </Typography>
        </Box>
        {isRunning && (
          <Chip
            label={
              task.progress.total > 0 ? `${Math.round(task.progress.percentage)}%` : 'Procesando...'
            }
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.72rem',
              height: 22,
              background: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
            }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
        {isRunning && (
          <Box>
            <StyledProgress
              variant={task.progress.total > 0 ? 'determinate' : 'indeterminate'}
              value={task.progress.total > 0 ? task.progress.percentage : undefined}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {task.progress.total > 0
                ? `${task.progress.current} de ${task.progress.total} registros`
                : 'Esperando respuesta del servidor...'}
            </Typography>
            <Tooltip title="Cancelar esta carga">
              <Button
                size="small"
                color="error"
                startIcon={<StopCircleIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  cancelTask(task.id);
                  onRemoved?.();
                }}
                sx={{ fontSize: '0.72rem', textTransform: 'none', mt: 0.5 }}>
                Cancelar
              </Button>
            </Tooltip>
          </Box>
        )}
        {(isCompleted || isError) && (
          <Box>
            {task.result && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {task.result.successCount > 0 && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label={`${task.result.successCount} guardados`}
                    size="small"
                    sx={{
                      background: alpha(theme.palette.success.main, 0.15),
                      color: theme.palette.success.dark,
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      height: 24,
                    }}
                  />
                )}
                {task.result.errorCount > 0 && (
                  <Chip
                    icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                    label={`${task.result.errorCount} errores`}
                    size="small"
                    sx={{
                      background: alpha(theme.palette.error.main, 0.15),
                      color: theme.palette.error.dark,
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      height: 24,
                    }}
                  />
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  navigate(task.detailsRoute || '/tesoreria/movimientos', {
                    state: {
                      backgroundJobId: task.jobId,
                      backgroundTaskResult: task.result,
                      backgroundFileName: task.fileName,
                    },
                  })
                }
                sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
                Ver detalles
              </Button>
              {isError && (
                <Tooltip title="Reintentar esta carga">
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      retryTask(task.id);
                    }}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
                    Reintentar
                  </Button>
                </Tooltip>
              )}
              <Button
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTask(task.id);
                  onRemoved?.();
                }}
                sx={{ fontSize: '0.72rem', textTransform: 'none', minWidth: 'auto' }}>
                Eliminar
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export const BackgroundTasksPopover: React.FC<BackgroundTasksPopoverProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { tasks, clearCompleted, hasActiveTasks, refreshTasks } = useBackgroundTasks();

  const runningTasks = tasks.filter((t) => t.status === 'running');
  const completedTasks = tasks.filter((t) => t.status !== 'running');
  const isEmpty = tasks.length === 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: 520,
            maxHeight: 560,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          },
        },
      }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: '#fff',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Cargas en segundo plano
          </Typography>
          {hasActiveTasks && (
            <Chip
              label={`${runningTasks.length} activa${runningTasks.length > 1 ? 's' : ''}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 700,
                backgroundColor: alpha('#fff', 0.25),
                color: '#fff',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Actualizar">
            <IconButton
              size="small"
              onClick={() => refreshTasks()}
              sx={{ color: '#fff', '&:hover': { backgroundColor: alpha('#fff', 0.15) } }}>
              <SyncIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: '#fff', '&:hover': { backgroundColor: alpha('#fff', 0.15) } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxHeight: 460, overflow: 'auto', p: 1 }}>
        {isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 5,
              color: 'text.secondary',
            }}>
            <InboxIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.6 }}>
              No hay cargas en segundo plano
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.4, mt: 0.5 }}>
              Las cargas masivas que envíes a segundo plano aparecerán aquí
            </Typography>
          </Box>
        ) : (
          <>
            {runningTasks.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', px: 1 }}>
                  EN PROCESO ({runningTasks.length})
                </Typography>
                {runningTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onRemoved={() => {
                      if (tasks.length <= 1) onClose();
                    }}
                  />
                ))}
              </Box>
            )}

            {completedTasks.length > 0 && (
              <Box>
                {runningTasks.length > 0 && <Divider sx={{ my: 1 }} />}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1,
                    mb: 0.5,
                  }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    COMPLETADAS ({completedTasks.length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CleaningServicesIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      clearCompleted();
                      if (runningTasks.length === 0) onClose();
                    }}
                    sx={{
                      fontSize: '0.7rem',
                      textTransform: 'none',
                      color: theme.palette.error.main,
                      fontWeight: 600,
                    }}>
                    Limpiar todo
                  </Button>
                </Box>
                {completedTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onRemoved={() => {
                      if (tasks.length <= 1) onClose();
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Popover>
  );
};
